import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Bell, Loader2, TrendingDown, TrendingUp, BarChart3, Clock, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALERT_TYPES = [
  {
    value: 'PRICE_DROP',
    label: 'Price Drop Alert',
    description: 'Notify when supplier price drops below threshold',
    icon: TrendingDown,
    hasThreshold: true,
    thresholdLabel: 'Target Price (£)',
  },
  {
    value: 'PROFIT_THRESHOLD',
    label: 'Profit Threshold Met',
    description: 'Notify when expected profit exceeds threshold',
    icon: TrendingUp,
    hasThreshold: true,
    thresholdLabel: 'Min Profit (£)',
  },
  {
    value: 'RANK_IMPROVEMENT',
    label: 'Rank Improvement',
    description: 'Notify when Amazon rank improves significantly',
    icon: BarChart3,
    hasThreshold: true,
    thresholdLabel: 'Target Rank',
  },
  {
    value: 'PROFIT_DROPPED',
    label: 'Profit Dropped',
    description: 'Notify when profit falls below minimum',
    icon: AlertTriangle,
    hasThreshold: true,
    thresholdLabel: 'Min Profit (£)',
  },
  {
    value: 'HIGH_IMPORT_RISK',
    label: 'High Import Risk',
    description: 'Notify when import costs increase significantly',
    icon: AlertTriangle,
    hasThreshold: false,
  },
];

export function CreateAlertModal({ open, onClose, book, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [alertType, setAlertType] = useState('PRICE_DROP');
  const [thresholdValue, setThresholdValue] = useState('');

  const selectedType = ALERT_TYPES.find(t => t.value === alertType);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!book) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/alerts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          book_id: book.book_id,
          alert_type: alertType,
          threshold_value: selectedType?.hasThreshold ? parseFloat(thresholdValue) : null,
        }),
      });

      if (response.ok) {
        onSuccess();
        onClose();
        setAlertType('PRICE_DROP');
        setThresholdValue('');
      }
    } catch (error) {
      console.error('Failed to create alert:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!book) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px]" data-testid="alert-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-gold" />
            Create Alert
          </DialogTitle>
        </DialogHeader>

        {/* Book Info */}
        <div className="p-3 bg-muted/50 rounded-lg border mb-4">
          <div className="font-medium">{book.title}</div>
          <div className="text-sm text-muted-foreground mt-1">
            ISBN: {book.isbn} • Current Price: {formatCurrency(book.used_price)}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="alert_type">Alert Type</Label>
            <Select value={alertType} onValueChange={setAlertType}>
              <SelectTrigger data-testid="select-alert-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ALERT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <type.icon className="h-4 w-4" />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedType && (
              <p className="text-xs text-muted-foreground">{selectedType.description}</p>
            )}
          </div>

          {selectedType?.hasThreshold && (
            <div className="space-y-2">
              <Label htmlFor="threshold">{selectedType.thresholdLabel}</Label>
              <Input
                id="threshold"
                type="number"
                step="0.01"
                placeholder="Enter value..."
                value={thresholdValue}
                onChange={(e) => setThresholdValue(e.target.value)}
                required
                data-testid="input-threshold"
              />
            </div>
          )}

          {/* Alert Preview */}
          <div className="p-3 bg-gold/5 border border-gold/20 rounded-lg">
            <div className="text-xs text-gold font-medium mb-1">Alert Preview</div>
            <div className="text-sm">
              {alertType === 'PRICE_DROP' && thresholdValue && (
                <>Alert when price drops below {formatCurrency(parseFloat(thresholdValue))}</>
              )}
              {alertType === 'PROFIT_THRESHOLD' && thresholdValue && (
                <>Alert when profit exceeds {formatCurrency(parseFloat(thresholdValue))}</>
              )}
              {alertType === 'RANK_IMPROVEMENT' && thresholdValue && (
                <>Alert when rank improves to {parseInt(thresholdValue).toLocaleString()} or better</>
              )}
              {alertType === 'PROFIT_DROPPED' && thresholdValue && (
                <>Alert when profit drops below {formatCurrency(parseFloat(thresholdValue))}</>
              )}
              {alertType === 'HIGH_IMPORT_RISK' && (
                <>Alert when import costs change significantly</>
              )}
              {!thresholdValue && selectedType?.hasThreshold && (
                <span className="text-muted-foreground">Enter a threshold value</span>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading || (selectedType?.hasThreshold && !thresholdValue)} 
              className="btn-gold"
              data-testid="submit-alert"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Alert
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
